import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { db } from '../firebase/config';
import TenantUrlHelper from '../services/TenantUrlHelper';
import { 
  collection, 
  query, 
  where, 
  getDocs, 
  orderBy, 
  limit,
  doc,
  updateDoc,
  serverTimestamp 
} from 'firebase/firestore';

const SubstituteManagerDashboard = ({ currentUser }) => {
  const [stats, setStats] = useState({
    totalSubstitutes: 0,
    pendingRegistrations: 0,
    activeSubRequests: 0,
    availableToday: 0
  });
  const [recentActivity, setRecentActivity] = useState([]);
  const [pendingRegistrations, setPendingRegistrations] = useState([]);
  const [upcomingSubRequests, setUpcomingSubRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedRequests, setExpandedRequests] = useState({});
  const [internalSubs, setInternalSubs] = useState([]);
  const [outsideSubs, setOutsideSubs] = useState([]);
  const [showInternalSubs, setShowInternalSubs] = useState(false);
  const [showOutsideSubs, setShowOutsideSubs] = useState(false);
  const [internalSortBy, setInternalSortBy] = useState('name');
  const [outsideSortBy, setOutsideSortBy] = useState('name');

  useEffect(() => {
    if (currentUser?.tenantId) {
      loadDashboardData();
    }
  }, [currentUser]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      
      const tenantId = currentUser.tenantId;
      
      // Load all data in parallel
      const [
        internalSubsData,
        outsideSubsData,
        registrationsData,
        subRequestsData
      ] = await Promise.all([
        loadInternalSubs(tenantId),
        loadOutsideSubs(tenantId),
        loadPendingRegistrations(tenantId),
        loadSubRequests(tenantId)
      ]);
      
      const allSubstitutes = [...internalSubsData, ...outsideSubsData];
      
      // Update stats
      setStats({
        totalSubstitutes: allSubstitutes.length,
        pendingRegistrations: registrationsData.length,
        activeSubRequests: subRequestsData.filter(req => !req.filled).length, // Unfilled requests
        availableToday: allSubstitutes.filter(sub => 
          sub.status === 'active' && 
          sub.availability && 
          Object.values(sub.availability).some(val => val)
        ).length
      });
      
      setInternalSubs(sortSubstitutes(internalSubsData, internalSortBy));
      setOutsideSubs(sortSubstitutes(outsideSubsData, outsideSortBy));
      
      setPendingRegistrations(registrationsData.slice(0, 5)); // Show first 5
      setUpcomingSubRequests(subRequestsData.slice(0, 5)); // Show first 5
      
      // Create recent activity feed with null safety
      const activities = [
        ...registrationsData.slice(0, 3).map(reg => ({
          type: 'registration',
          message: `New substitute application: ${reg.firstName || 'Unknown'} ${reg.lastName || 'User'}`,
          timestamp: reg.submittedAt,
          id: reg.id
        })),
        ...subRequestsData.slice(0, 2).map(req => ({
          type: 'sub_request',
          message: `Sub request by ${req.name || 'Unknown'} on ${req.date || 'Unknown date'}`,
          timestamp: req.createdAt || req.submittedAt,
          id: req.id
        }))
      ].sort((a, b) => {
        const aTime = a.timestamp?.toDate() || new Date(0);
        const bTime = b.timestamp?.toDate() || new Date(0);
        return bTime - aTime;
      });
      
      setRecentActivity(activities.slice(0, 5));
      
    } catch (err) {
      console.error('Error loading dashboard data:', err);
      setError('Failed to load dashboard data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const loadInternalSubs = async (tenantId) => {
    const q = query(
      collection(db, 'faculty_staff'),
      where('tenantId', '==', tenantId)
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })).filter(person => 
      person.role !== 'Outside Sub' // Everyone except Outside Subs
    );
  };

  const loadOutsideSubs = async (tenantId) => {
    const q = query(
      collection(db, 'faculty_staff'),
      where('tenantId', '==', tenantId),
      where('role', '==', 'Outside Sub')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  };

  const loadPendingRegistrations = async (tenantId) => {
    const q = query(
      collection(db, 'substituteRegistrations'),
      where('tenantId', '==', tenantId),
      where('status', '==', 'pending'),
      orderBy('submittedAt', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  };

  const loadSubRequests = async (tenantId) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const q = query(
      collection(db, 'sub_requests'),
      where('tenantId', '==', tenantId),
      orderBy('date', 'desc'),
      limit(20)
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  };

  const quickApproveRegistration = async (registrationId) => {
    try {
      await updateDoc(doc(db, 'substituteRegistrations', registrationId), {
        status: 'approved',
        approvedAt: serverTimestamp(),
        approvedBy: currentUser.uid
      });
      
      // Refresh data
      loadDashboardData();
      
    } catch (err) {
      console.error('Error approving registration:', err);
      setError('Failed to approve registration. Please try again.');
    }
  };

  const toggleRequestDetails = (requestId) => {
    setExpandedRequests(prev => ({
      ...prev,
      [requestId]: !prev[requestId]
    }));
  };

  const sortSubstitutes = (subs, sortBy) => {
    const sorted = [...subs];
    
    switch (sortBy) {
      case 'name':
        return sorted.sort((a, b) => {
          const nameA = `${a.lastName || ''} ${a.firstName || ''}`.trim().toLowerCase();
          const nameB = `${b.lastName || ''} ${b.firstName || ''}`.trim().toLowerCase();
          return nameA.localeCompare(nameB);
        });
      case 'name-desc':
        return sorted.sort((a, b) => {
          const nameA = `${a.lastName || ''} ${a.firstName || ''}`.trim().toLowerCase();
          const nameB = `${b.lastName || ''} ${b.firstName || ''}`.trim().toLowerCase();
          return nameB.localeCompare(nameA);
        });
      case 'department':
        return sorted.sort((a, b) => {
          const deptA = (a.department || 'ZZ Unknown').toLowerCase();
          const deptB = (b.department || 'ZZ Unknown').toLowerCase();
          return deptA.localeCompare(deptB);
        });
      case 'division':
        return sorted.sort((a, b) => {
          const divA = (a.division || 'ZZ Unknown').toLowerCase();
          const divB = (b.division || 'ZZ Unknown').toLowerCase();
          return divA.localeCompare(divB);
        });
      case 'role':
        return sorted.sort((a, b) => {
          const roleA = (a.role || 'ZZ Unknown').toLowerCase();
          const roleB = (b.role || 'ZZ Unknown').toLowerCase();
          return roleA.localeCompare(roleB);
        });
      case 'status':
        return sorted.sort((a, b) => {
          const statusA = a.status === 'active' ? 0 : 1;
          const statusB = b.status === 'active' ? 0 : 1;
          return statusA - statusB;
        });
      case 'experience':
        return sorted.sort((a, b) => {
          const expA = parseInt(a.yearsExperience) || 0;
          const expB = parseInt(b.yearsExperience) || 0;
          return expB - expA; // Descending (most experienced first)
        });
      default:
        return sorted;
    }
  };

  const handleInternalSort = (sortBy) => {
    setInternalSortBy(sortBy);
    setInternalSubs(current => sortSubstitutes(current, sortBy));
  };

  const handleOutsideSort = (sortBy) => {
    setOutsideSortBy(sortBy);
    setOutsideSubs(current => sortSubstitutes(current, sortBy));
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Unknown date';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatDateTime = (timestamp) => {
    if (!timestamp) return 'Unknown time';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="dashboard-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading substitute management dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <h1>Substitute Management Dashboard</h1>
        <p className="dashboard-subtitle">
          Comprehensive substitute teacher management and oversight
        </p>
        <div className="header-actions">
          <Link to="/request-sub" className="btn btn-primary">
            Request Substitute
          </Link>
          <Link to="/substitute-approvals" className="btn btn-success">
            Review Applications
          </Link>
        </div>
      </div>

      {error && (
        <div className="error-message">
          <h3>Error</h3>
          <p>{error}</p>
          <button className="btn btn-primary" onClick={loadDashboardData}>
            Try Again
          </button>
        </div>
      )}

      {/* Stats Cards */}
      <div className="metrics-grid">
        <div className="metric-card metric-success">
          <div className="metric-icon">👨‍🏫</div>
          <div className="metric-content">
            <h3>Active Substitutes</h3>
            <div className="metric-value">{stats.totalSubstitutes}</div>
            <p>Registered and approved</p>
          </div>
        </div>

        <div className="metric-card metric-warning">
          <div className="metric-icon">📝</div>
          <div className="metric-content">
            <h3>Pending Applications</h3>
            <div className="metric-value">{stats.pendingRegistrations}</div>
            <p>Awaiting review</p>
          </div>
          {stats.pendingRegistrations > 0 && (
            <Link to="/substitute-approvals" className="metric-link">
              Review Now →
            </Link>
          )}
        </div>

        <div className="metric-card metric-danger">
          <div className="metric-icon">🚨</div>
          <div className="metric-content">
            <h3>Open Sub Requests</h3>
            <div className="metric-value">{stats.activeSubRequests}</div>
            <p>Need coverage</p>
          </div>
          <Link to="/available-signups" className="metric-link">
            View Open Requests →
          </Link>
        </div>

        <div className="metric-card metric-info">
          <div className="metric-icon">✅</div>
          <div className="metric-content">
            <h3>Available Today</h3>
            <div className="metric-value">{stats.availableToday}</div>
            <p>Ready for assignments</p>
          </div>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="content-grid">
        
        {/* Recent Activity */}
        <div className="dashboard-card">
          <div className="card-header">
            <h3>Recent Activity</h3>
          </div>
          {recentActivity.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">📭</div>
              <h4>No Recent Activity</h4>
              <p>Activity will appear here as substitutes apply and sub requests are created.</p>
            </div>
          ) : (
            <div className="activity-list">
              {recentActivity.map((activity, index) => (
                <div key={index} className="activity-item">
                  <div className="activity-icon">
                    {activity.type === 'registration' ? '👨‍🏫' : '📝'}
                  </div>
                  <div className="activity-content">
                    <div className="activity-message">{activity.message}</div>
                    <div className="activity-time">
                      {formatDateTime(activity.timestamp)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Pending Registrations */}
        <div className="dashboard-card">
          <div className="card-header">
            <h3>Pending Applications</h3>
            {pendingRegistrations.length > 0 && (
              <Link to="/substitute-approvals" className="header-link">
                View All →
              </Link>
            )}
          </div>
          
          {pendingRegistrations.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">✅</div>
              <h4>No Pending Applications</h4>
              <p>All substitute teacher applications have been processed.</p>
            </div>
          ) : (
            <div className="registrations-list">
              {pendingRegistrations.map((registration) => (
                <div key={registration.id} className="registration-item">
                  <div className="registration-info">
                    <div className="registration-name">
                      {registration.firstName || 'Unknown'} {registration.lastName || 'User'}
                    </div>
                    <div className="registration-email">
                      {registration.email || 'No email provided'}
                    </div>
                    <div className="registration-details">
                      <span>Experience: {registration.yearsExperience || 'Not specified'}</span>
                      <span>Submitted: {formatDate(registration.submittedAt)}</span>
                    </div>
                    {registration.ageGroupPreferences && registration.ageGroupPreferences.length > 0 && (
                      <div className="registration-preferences">
                        <strong>Age Groups:</strong> {Array.isArray(registration.ageGroupPreferences) 
                          ? registration.ageGroupPreferences.join(', ') 
                          : registration.ageGroupPreferences}
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => quickApproveRegistration(registration.id)}
                    className="btn btn-success btn-sm"
                  >
                    Quick Approve
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Recent Sub Requests */}
      <div className="dashboard-card">
        <div className="card-header">
          <h3>Recent Sub Requests</h3>
          <div className="header-actions">
            <Link to="/available-signups" className="header-link">
              View All →
            </Link>
            <Link to="/request-sub" className="btn btn-success btn-sm">
              + Create Request
            </Link>
          </div>
        </div>
        
        {upcomingSubRequests.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📝</div>
            <h4>No Recent Sub Requests</h4>
            <p>Sub requests will appear here as they are created.</p>
            <Link to="/request-sub" className="btn btn-primary">
              Create First Request
            </Link>
          </div>
        ) : (
          <div className="requests-grid">
            {upcomingSubRequests.map((request) => (
              <div key={request.id} className="request-item">
                <div className="request-header">
                  <h4>{request.name || 'Unknown Teacher'}</h4>
                  <span className={`status-badge status-${request.filled ? 'filled' : 'pending'}`}>
                    {request.filled ? 'Filled' : 'Pending'}
                  </span>
                </div>
                <div className="request-details">
                  <div className="request-detail">
                    📅 {request.date || 'Date not specified'}
                  </div>
                  <div className="request-detail">
                    📍 {request.room || 'Room not specified'}
                  </div>
                  <div className="request-detail">
                    🏫 {request.department || 'Department not specified'}
                  </div>
                  {request.periods && request.periods.length > 0 && (
                    <div className="request-detail">
                      <strong>Periods:</strong> {Array.isArray(request.periods) 
                        ? request.periods.join(', ') 
                        : request.periods}
                    </div>
                  )}
                  {request.reason && (
                    <div className="request-detail">
                      <strong>Reason:</strong> {request.reason.substring(0, 50)}
                      {request.reason.length > 50 ? '...' : ''}
                    </div>
                  )}
                  {request.filled && (
                    <div className="request-detail">
                      <strong>Covered by:</strong> {
                        Object.values(request.filled)
                          .map(sub => sub.name)
                          .filter((name, index, arr) => arr.indexOf(name) === index)
                          .join(', ')
                      }
                    </div>
                  )}
                </div>
                
                {/* Expandable details section */}
                {expandedRequests[request.id] && (
                  <div className="expanded-details">
                    <h5>Additional Information</h5>
                    {request.requesterEmail && (
                      <div className="detail-item">
                        <strong>Email:</strong> {request.requesterEmail}
                      </div>
                    )}
                    {request.submittedByPrivilegedUserName && (
                      <div className="detail-item">
                        <strong>Submitted by:</strong> {request.submittedByPrivilegedUserName}
                      </div>
                    )}
                    {request.periodNotes && Object.keys(request.periodNotes).length > 0 && (
                      <div className="detail-item">
                        <strong>Period Notes:</strong>
                        <ul>
                          {Object.entries(request.periodNotes).map(([period, note]) => (
                            <li key={period}><strong>{period}:</strong> {note}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {request.filled && (
                      <div className="detail-item">
                        <strong>Assignment Details:</strong>
                        <ul>
                          {Object.entries(request.filled).map(([period, sub]) => (
                            <li key={period}>
                              <strong>{period}:</strong> {sub.name} ({sub.email})
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {request.createdAt && (
                      <div className="detail-item">
                        <strong>Created:</strong> {formatDateTime(request.createdAt)}
                      </div>
                    )}
                  </div>
                )}
                
                <div className="request-actions">
                  <button 
                    onClick={() => toggleRequestDetails(request.id)}
                    className="btn btn-primary btn-sm"
                  >
                    {expandedRequests[request.id] ? 'Hide Details' : 'View Details'}
                  </button>
                  {!request.filled && (
                    <Link to="/substitute-directory" className="btn btn-success btn-sm">
                      Assign Sub
                    </Link>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Substitute Directory */}
      <div className="dashboard-card">
        <div className="card-header">
          <h3>Substitute Directory</h3>
          <span className="directory-count">
            {internalSubs.length + outsideSubs.length} Total Subs
          </span>
        </div>
        
        <div className="directory-content">
          {/* Internal Subs Section */}
          <div className="sub-category">
            <button 
              className="category-header"
              onClick={() => setShowInternalSubs(!showInternalSubs)}
            >
              <span className="category-title">
                Internal Subs ({internalSubs.length})
              </span>
              <div className="header-controls">
                {showInternalSubs && (
                  <select 
                    value={internalSortBy}
                    onChange={(e) => handleInternalSort(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    className="sort-dropdown"
                  >
                    <option value="name">Sort by Name (A-Z)</option>
                    <option value="name-desc">Sort by Name (Z-A)</option>
                    <option value="department">Sort by Department</option>
                    <option value="division">Sort by Division</option>
                    <option value="role">Sort by Role</option>
                    <option value="status">Sort by Status</option>
                  </select>
                )}
                <span className={`dropdown-arrow ${showInternalSubs ? 'expanded' : ''}`}>
                  ▼
                </span>
              </div>
            </button>
            
            {showInternalSubs && (
              <div className="sub-list">
                {internalSubs.length === 0 ? (
                  <div className="empty-category">
                    No internal substitutes available
                  </div>
                ) : (
                  internalSubs.map((sub) => (
                    <div key={sub.id} className="sub-item">
                      <div className="sub-info">
                        <div className="sub-name">
                          {sub.firstName || 'Unknown'} {sub.lastName || 'User'}
                        </div>
                        <div className="sub-details">
                          <span className="sub-email">📧 {sub.email || 'No email'}</span>
                          {sub.phone && (
                            <span className="sub-phone">📱 {sub.phone}</span>
                          )}
                          <span className="sub-department">🏫 {sub.department || 'Not specified'}</span>
                          <span className="sub-role">👤 {sub.role || 'Not specified'}</span>
                        </div>
                        {sub.ageGroupPreferences && sub.ageGroupPreferences.length > 0 && (
                          <div className="sub-preferences">
                            <strong>Preferred Ages:</strong> {Array.isArray(sub.ageGroupPreferences) 
                              ? sub.ageGroupPreferences.join(', ') 
                              : sub.ageGroupPreferences}
                          </div>
                        )}
                        {sub.subjectSpecialties && sub.subjectSpecialties.length > 0 && (
                          <div className="sub-specialties">
                            <strong>Specialties:</strong> {Array.isArray(sub.subjectSpecialties) 
                              ? sub.subjectSpecialties.join(', ') 
                              : sub.subjectSpecialties}
                          </div>
                        )}
                      </div>
                      <div className="sub-status">
                        <span className={`status-indicator ${sub.status === 'active' ? 'active' : 'inactive'}`}>
                          {sub.status === 'active' ? 'Available' : 'Unavailable'}
                        </span>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>

          {/* Outside Subs Section */}
          <div className="sub-category">
            <button 
              className="category-header"
              onClick={() => setShowOutsideSubs(!showOutsideSubs)}
            >
              <span className="category-title">
                Outside Subs ({outsideSubs.length})
              </span>
              <div className="header-controls">
                {showOutsideSubs && (
                  <select 
                    value={outsideSortBy}
                    onChange={(e) => handleOutsideSort(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    className="sort-dropdown"
                  >
                    <option value="name">Sort by Name (A-Z)</option>
                    <option value="name-desc">Sort by Name (Z-A)</option>
                    <option value="experience">Sort by Experience</option>
                    <option value="status">Sort by Status</option>
                  </select>
                )}
                <span className={`dropdown-arrow ${showOutsideSubs ? 'expanded' : ''}`}>
                  ▼
                </span>
              </div>
            </button>
            
            {showOutsideSubs && (
              <div className="sub-list">
                {outsideSubs.length === 0 ? (
                  <div className="empty-category">
                    No outside substitutes registered
                  </div>
                ) : (
                  outsideSubs.map((sub) => (
                    <div key={sub.id} className="sub-item">
                      <div className="sub-info">
                        <div className="sub-name">
                          {sub.firstName || 'Unknown'} {sub.lastName || 'User'}
                        </div>
                        <div className="sub-details">
                          <span className="sub-email">📧 {sub.email || 'No email'}</span>
                          {sub.phone && (
                            <span className="sub-phone">📱 {sub.phone}</span>
                          )}
                          {sub.yearsExperience && (
                            <span className="sub-experience">📚 {sub.yearsExperience} years experience</span>
                          )}
                          {sub.emergencyContact && (
                            <span className="sub-emergency">🚨 Emergency: {sub.emergencyContact}</span>
                          )}
                        </div>
                        {sub.ageGroupPreferences && sub.ageGroupPreferences.length > 0 && (
                          <div className="sub-preferences">
                            <strong>Preferred Ages:</strong> {Array.isArray(sub.ageGroupPreferences) 
                              ? sub.ageGroupPreferences.join(', ') 
                              : sub.ageGroupPreferences}
                          </div>
                        )}
                        {sub.subjectSpecialties && sub.subjectSpecialties.length > 0 && (
                          <div className="sub-specialties">
                            <strong>Specialties:</strong> {Array.isArray(sub.subjectSpecialties) 
                              ? sub.subjectSpecialties.join(', ') 
                              : sub.subjectSpecialties}
                          </div>
                        )}
                        {sub.availability && (
                          <div className="sub-availability">
                            <strong>Available Days:</strong> {
                              (() => {
                                const dayOrder = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
                                const availableDays = Object.entries(sub.availability)
                                  .filter(([day, available]) => available)
                                  .map(([day]) => day.toLowerCase())
                                  .sort((a, b) => dayOrder.indexOf(a) - dayOrder.indexOf(b))
                                  .map(day => day.charAt(0).toUpperCase() + day.slice(1));
                                
                                return availableDays.length > 0 ? availableDays.join(', ') : 'None specified';
                              })()
                            }
                          </div>
                        )}
                      </div>
                      <div className="sub-status">
                        <span className={`status-indicator ${sub.status === 'active' ? 'active' : 'inactive'}`}>
                          {sub.status === 'active' ? 'Available' : 'Unavailable'}
                        </span>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="dashboard-card">
        <div className="card-header">
          <h3>Quick Actions</h3>
        </div>
        <div className="quick-actions-grid">
          <Link to="/request-sub" className="quick-action">
            <div className="quick-action-icon">📝</div>
            <div className="quick-action-content">
              <div className="quick-action-title">Request Substitute</div>
              <div className="quick-action-subtitle">Create new sub request</div>
            </div>
          </Link>

          <Link to="/substitute-directory" className="quick-action">
            <div className="quick-action-icon">👨‍🏫</div>
            <div className="quick-action-content">
              <div className="quick-action-title">Browse Substitutes</div>
              <div className="quick-action-subtitle">View substitute directory</div>
            </div>
          </Link>

          <Link to="/substitute-approvals" className="quick-action">
            <div className="quick-action-icon">✅</div>
            <div className="quick-action-content">
              <div className="quick-action-title">Review Applications</div>
              <div className="quick-action-subtitle">
                {stats.pendingRegistrations > 0 ? `${stats.pendingRegistrations} pending` : 'All caught up!'}
              </div>
            </div>
          </Link>

          <Link to="/available-signups" className="quick-action">
            <div className="quick-action-icon">🚨</div>
            <div className="quick-action-content">
              <div className="quick-action-title">Open Requests</div>
              <div className="quick-action-subtitle">
                {stats.activeSubRequests > 0 ? `${stats.activeSubRequests} need coverage` : 'All covered!'}
              </div>
            </div>
          </Link>
        </div>
      </div>

      {/* Help Section */}
      <div className="help-card">
        <h4>Getting Started with Substitute Teacher Management</h4>
        <div className="help-grid">
          <div className="help-item">
            <strong>1. Review Applications:</strong> Approve or deny substitute teacher applications from the public registration form.
          </div>
          <div className="help-item">
            <strong>2. Manage Directory:</strong> Browse your approved substitute teachers and their qualifications.
          </div>
          <div className="help-item">
            <strong>3. Handle Requests:</strong> View and assign substitutes to open sub requests from teachers.
          </div>
          <div className="help-item">
            <strong>4. Monitor Coverage:</strong> Track substitute assignments and ensure all classes are covered.
          </div>
        </div>
        <div className="help-url">
          <p>
            <strong>Public Registration URL:</strong> 
            <code>
              {TenantUrlHelper.getPublicRegistrationUrl('substitute', window.location.hostname.split('.')[0])}
            </code>
          </p>
          <p className="help-note">
            Share this URL with potential substitute teachers. 
            {TenantUrlHelper.isDevelopment() ? ' (Development URL - will be clean in production)' : ''}
          </p>
        </div>
      </div>

      <style jsx>{`
        .dashboard-container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 20px;
          background-color: #f8f9fa;
          min-height: 100vh;
        }

        .dashboard-header {
          text-align: center;
          margin-bottom: 30px;
          padding: 30px;
          background: linear-gradient(135deg, #28a745 0%, #20c997 100%);
          color: white;
          border-radius: 15px;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
        }

        .dashboard-header h1 {
          margin: 0;
          font-size: 2.5rem;
          font-weight: 700;
          text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }

        .dashboard-subtitle {
          margin: 15px 0 20px 0;
          opacity: 0.9;
          font-size: 1.1rem;
          font-weight: 300;
        }

        .header-actions {
          display: flex;
          gap: 15px;
          justify-content: center;
          flex-wrap: wrap;
        }

        .metrics-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: 20px;
          margin-bottom: 30px;
        }

        .metric-card {
          background: white;
          border-radius: 15px;
          padding: 25px;
          display: flex;
          align-items: center;
          gap: 20px;
          box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
          transition: transform 0.2s ease, box-shadow 0.2s ease;
          border-left: 5px solid;
          position: relative;
        }

        .metric-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
        }

        .metric-success { border-left-color: #28a745; }
        .metric-warning { border-left-color: #ffc107; }
        .metric-danger { border-left-color: #dc3545; }
        .metric-info { border-left-color: #17a2b8; }

        .metric-icon {
          font-size: 3rem;
          opacity: 0.8;
        }

        .metric-content h3 {
          margin: 0 0 10px 0;
          color: #2c3e50;
          font-size: 1.1rem;
          font-weight: 600;
        }

        .metric-value {
          font-size: 2.5rem;
          font-weight: 700;
          color: #2c3e50;
          margin: 5px 0;
        }

        .metric-content p {
          margin: 0;
          color: #6c757d;
          font-size: 0.9rem;
        }

        .metric-link {
          position: absolute;
          bottom: 10px;
          right: 15px;
          color: #007bff;
          font-size: 0.8rem;
          text-decoration: none;
          font-weight: 600;
        }

        .metric-link:hover {
          text-decoration: underline;
        }

        .content-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 20px;
          margin-bottom: 30px;
        }

        .dashboard-card {
          background: white;
          border-radius: 15px;
          box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
          overflow: hidden;
          transition: transform 0.2s ease, box-shadow 0.2s ease;
        }

        .dashboard-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
        }

        .card-header {
          padding: 20px 25px;
          background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
          border-bottom: 1px solid #dee2e6;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .card-header h3 {
          margin: 0;
          color: #2c3e50;
          font-size: 1.3rem;
          font-weight: 600;
        }

        .header-link {
          color: #007bff;
          text-decoration: none;
          font-size: 0.9rem;
          font-weight: 500;
        }

        .header-link:hover {
          text-decoration: underline;
        }

        .header-actions {
          display: flex;
          gap: 10px;
          align-items: center;
        }

        .empty-state {
          text-align: center;
          padding: 60px 20px;
          color: #6c757d;
        }

        .empty-icon {
          font-size: 4rem;
          margin-bottom: 20px;
          opacity: 0.7;
        }

        .empty-state h4 {
          margin: 0 0 10px 0;
          font-size: 1.3rem;
          font-weight: 600;
          color: #495057;
        }

        .empty-state p {
          margin: 0 0 20px 0;
          font-size: 1rem;
        }

        .activity-list {
          padding: 20px;
        }

        .activity-item {
          display: flex;
          align-items: center;
          gap: 15px;
          padding: 15px 0;
          border-bottom: 1px solid #f1f3f4;
        }

        .activity-item:last-child {
          border-bottom: none;
        }

        .activity-icon {
          font-size: 1.5rem;
        }

        .activity-content {
          flex: 1;
        }

        .activity-message {
          font-size: 0.95rem;
          color: #2c3e50;
          margin-bottom: 5px;
        }

        .activity-time {
          font-size: 0.8rem;
          color: #6c757d;
        }

        .registrations-list {
          padding: 20px;
        }

        .registration-item {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          padding: 20px;
          margin-bottom: 15px;
          background: #f8f9fa;
          border-radius: 10px;
          border: 1px solid #e9ecef;
        }

        .registration-info {
          flex: 1;
        }

        .registration-name {
          font-weight: 600;
          color: #2c3e50;
          margin-bottom: 5px;
        }

        .registration-email {
          color: #6c757d;
          font-size: 0.9rem;
          margin-bottom: 8px;
        }

        .registration-details {
          display: flex;
          flex-direction: column;
          gap: 3px;
          font-size: 0.8rem;
          color: #6c757d;
        }

        .registration-preferences {
          margin-top: 8px;
          font-size: 0.8rem;
          color: #6c757d;
        }

        .requests-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 20px;
          padding: 20px;
        }

        .request-item {
          border: 1px solid #dee2e6;
          border-radius: 10px;
          padding: 20px;
          background: #f8f9fa;
        }

        .request-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 15px;
        }

        .request-header h4 {
          margin: 0;
          color: #2c3e50;
          font-size: 1.1rem;
        }

        .status-badge {
          padding: 4px 8px;
          border-radius: 12px;
          font-size: 0.8rem;
          font-weight: 500;
          text-transform: capitalize;
        }

        .status-pending {
          background-color: #ffc107;
          color: #212529;
        }

        .status-filled {
          background-color: #28a745;
          color: white;
        }

        .request-details {
          margin-bottom: 15px;
        }

        .request-detail {
          font-size: 0.9rem;
          color: #6c757d;
          margin-bottom: 5px;
        }

        .request-actions {
          display: flex;
          gap: 10px;
        }

        .expanded-details {
          margin-top: 15px;
          padding: 15px;
          background: white;
          border-radius: 8px;
          border: 1px solid #e9ecef;
        }

        .expanded-details h5 {
          margin: 0 0 15px 0;
          color: #2c3e50;
          font-size: 1rem;
          font-weight: 600;
        }

        .detail-item {
          margin-bottom: 10px;
          font-size: 0.9rem;
          color: #495057;
        }

        .detail-item strong {
          color: #2c3e50;
        }

        .detail-item ul {
          margin: 5px 0 0 20px;
          padding: 0;
        }

        .detail-item li {
          margin-bottom: 5px;
        }

        .directory-content {
          padding: 20px;
        }

        .directory-count {
          font-size: 0.9rem;
          color: #6c757d;
          font-weight: 500;
        }

        .sub-category {
          margin-bottom: 20px;
          border: 1px solid #e9ecef;
          border-radius: 10px;
          overflow: hidden;
        }

        .category-header {
          width: 100%;
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 15px 20px;
          background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
          border: none;
          cursor: pointer;
          transition: background-color 0.2s ease;
        }

        .category-header:hover {
          background: linear-gradient(135deg, #e9ecef 0%, #dee2e6 100%);
        }

        .header-controls {
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .sort-dropdown {
          padding: 5px 10px;
          border: 1px solid #dee2e6;
          border-radius: 5px;
          background: white;
          font-size: 0.85rem;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .sort-dropdown:hover {
          border-color: #007bff;
        }

        .sort-dropdown:focus {
          outline: none;
          border-color: #007bff;
          box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
        }

        .category-title {
          font-weight: 600;
          color: #2c3e50;
          font-size: 1.1rem;
        }

        .dropdown-arrow {
          font-size: 0.8rem;
          color: #6c757d;
          transition: transform 0.3s ease;
        }

        .dropdown-arrow.expanded {
          transform: rotate(180deg);
        }

        .sub-list {
          background: white;
        }

        .empty-category {
          padding: 30px;
          text-align: center;
          color: #6c757d;
          font-style: italic;
        }

        .sub-item {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          padding: 20px;
          border-bottom: 1px solid #f1f3f4;
          transition: background-color 0.2s ease;
        }

        .sub-item:last-child {
          border-bottom: none;
        }

        .sub-item:hover {
          background-color: #f8f9fa;
        }

        .sub-info {
          flex: 1;
        }

        .sub-name {
          font-weight: 600;
          color: #2c3e50;
          font-size: 1.1rem;
          margin-bottom: 10px;
        }

        .sub-details {
          display: flex;
          flex-wrap: wrap;
          gap: 15px;
          margin-bottom: 10px;
        }

        .sub-email, .sub-phone, .sub-department, .sub-role, .sub-experience, .sub-emergency {
          font-size: 0.9rem;
          color: #6c757d;
          display: flex;
          align-items: center;
          gap: 5px;
        }

        .sub-preferences, .sub-specialties, .sub-availability {
          margin-bottom: 8px;
          font-size: 0.9rem;
          color: #495057;
        }

        .sub-preferences strong, .sub-specialties strong, .sub-availability strong {
          color: #2c3e50;
        }

        .sub-status {
          margin-left: 20px;
        }

        .status-indicator {
          padding: 6px 12px;
          border-radius: 20px;
          font-size: 0.8rem;
          font-weight: 500;
          text-align: center;
          min-width: 80px;
          display: inline-block;
        }

        .status-indicator.active {
          background-color: #d4edda;
          color: #155724;
          border: 1px solid #c3e6cb;
        }

        .status-indicator.inactive {
          background-color: #f8d7da;
          color: #721c24;
          border: 1px solid #f5c6cb;
        }

        .quick-actions-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 20px;
          padding: 20px;
        }

        .quick-action {
          display: flex;
          align-items: center;
          gap: 15px;
          padding: 20px;
          border: 2px solid #dee2e6;
          border-radius: 10px;
          text-decoration: none;
          background: #f8f9fa;
          transition: all 0.2s ease;
        }

        .quick-action:hover {
          border-color: #007bff;
          background: white;
          transform: translateY(-2px);
          box-shadow: 0 4px 15px rgba(0, 123, 255, 0.1);
          text-decoration: none;
        }

        .quick-action-icon {
          font-size: 2rem;
        }

        .quick-action-title {
          font-weight: 600;
          color: #2c3e50;
          margin-bottom: 5px;
        }

        .quick-action-subtitle {
          font-size: 0.8rem;
          color: #6c757d;
        }

        .help-card {
          background: linear-gradient(135deg, #e7f3ff 0%, #b8daff 100%);
          border: 1px solid #b8daff;
          border-radius: 15px;
          padding: 25px;
          margin-top: 20px;
        }

        .help-card h4 {
          margin: 0 0 20px 0;
          color: #004085;
          font-size: 1.3rem;
        }

        .help-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 15px;
          margin-bottom: 20px;
        }

        .help-item {
          font-size: 0.9rem;
          color: #004085;
        }

        .help-url {
          background: white;
          border-radius: 8px;
          padding: 15px;
        }

        .help-url p {
          margin: 0 0 10px 0;
          font-size: 0.9rem;
          color: #004085;
        }

        .help-url code {
          background: #f8f9fa;
          padding: 4px 8px;
          border-radius: 4px;
          font-size: 0.85rem;
          margin-left: 5px;
        }

        .help-note {
          font-size: 0.8rem !important;
          color: #6c757d !important;
        }

        .btn {
          border: none;
          border-radius: 8px;
          padding: 10px 20px;
          font-size: 0.9rem;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
          text-decoration: none;
          display: inline-flex;
          align-items: center;
          justify-content: center;
        }

        .btn-sm {
          padding: 6px 12px;
          font-size: 0.85rem;
        }

        .btn-primary {
          background-color: #007bff;
          color: white;
        }

        .btn-primary:hover {
          background-color: #0056b3;
          transform: translateY(-1px);
          text-decoration: none;
          color: white;
        }

        .btn-success {
          background-color: #28a745;
          color: white;
        }

        .btn-success:hover {
          background-color: #218838;
          transform: translateY(-1px);
          text-decoration: none;
          color: white;
        }

        .loading-spinner {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          min-height: 400px;
          text-align: center;
        }

        .spinner {
          width: 50px;
          height: 50px;
          border: 5px solid #f3f3f3;
          border-top: 5px solid #28a745;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin-bottom: 20px;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        .error-message {
          text-align: center;
          padding: 40px;
          background: white;
          border-radius: 15px;
          box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
          color: #dc3545;
          margin-bottom: 30px;
        }

        .error-message h3 {
          margin-bottom: 15px;
          font-size: 1.5rem;
        }

        @media (max-width: 768px) {
          .dashboard-container {
            padding: 15px;
          }

          .dashboard-header {
            padding: 20px;
          }

          .dashboard-header h1 {
            font-size: 2rem;
          }

          .header-actions {
            flex-direction: column;
            gap: 10px;
          }

          .content-grid {
            grid-template-columns: 1fr;
          }

          .metrics-grid {
            grid-template-columns: 1fr;
          }

          .requests-grid {
            grid-template-columns: 1fr;
          }

          .quick-actions-grid {
            grid-template-columns: 1fr;
          }

          .help-grid {
            grid-template-columns: 1fr;
          }

          .metric-card {
            flex-direction: column;
            text-align: center;
            gap: 15px;
          }

          .quick-action {
            justify-content: center;
            text-align: center;
          }

          .request-header {
            flex-direction: column;
            align-items: flex-start;
            gap: 10px;
          }

          .request-actions {
            flex-direction: column;
          }

          .registration-item {
            flex-direction: column;
            gap: 15px;
            align-items: stretch;
          }
        }
      `}</style>
    </div>
  );
};

export default SubstituteManagerDashboard;